import React, { useEffect, useState, createContext, useContext, useRef } from "react";
import Card from "@mui/material/Card";
import { useSearchParams } from "react-router-dom";
import DashboardLayout from "examples/LayoutContainers/DashboardLayout";
import DashboardNavbar from "examples/Navbars/DashboardNavbar";
import { useLocation } from "react-router-dom";
import SearchStringOrIntReq5 from "../../../../Classes/Models/Requests/SearchStringOrIntReq";
import SessionsApi from "../../../../Classes/API/Administration/SessionsApi";
import PackageDetailsCard from "./PackageDetailsCard";
import AddSessionsToPackage from "./AddSessionsToPackage";
import PackageStatistics from "./PackageStatistics";
import "./PackageDetails.scss";
import PostPaid from "./PostPaid";
import NotesAndStartDate from "./NotesAndStartDate";
import AccountingApi from "Classes/API/AccountingApi";
import SearchStringOrIntReq from "Classes/Models/Requests/StringIntSearchReq";
import { UserContext } from "App";
import Swal from "sweetalert2";
import PackageSessionsTable from "./PackageSessionsTable";
import { getTableRecordsForPackage, getBalanceHistoryForPackage } from "Classes/API/ReceiptsApi";
import ReceiptsTable from "layouts/receipts/receipts-table";
import BalanceHistoryTable from "../PackageDetails/BalanceHistoryTable";

const Index = () => {
  const CurrentUser = useContext(UserContext);
  let { state } = useLocation();
  const [iIsLoaded, setIsLoaded] = useState(false);
  const [packageData, setPackageData] = useState({});
  const [loadError, setLoadError] = useState(null);
  const [showSessions, setShowSessions] = useState(false);
  const [showAddSessions, setShowAddSessions] = useState(false);
  const [userBalance, setUserBalance] = useState(null);
  const [balanceHistory, setBalanceHistory] = useState(null);
  const [receipts, setReceipts] = useState();
  const [searchParams] = useSearchParams();

  // Fetch package data
  useEffect(() => {
    const getPackageData = async (id) => {
      let req = new SearchStringOrIntReq5();
      req.SearchInt = id;
      try {
        let res = await SessionsApi.GetPackageById(req);
        if (res.status == "Success") {
          setLoadError(null);
          setPackageData(res.data);
        } else {
          setLoadError(res.message || "Failed to load package");
        }
      } catch (error) {
        console.error("Error fetching package:", error);
        setLoadError("Failed to load package");
      }
    };

    if (!iIsLoaded && state?.pkg?.id) {
      getPackageData(state.pkg.id);
    }
  }, [iIsLoaded, state]);

  // Fetch user balance
  useEffect(() => {
    const getUserBalance = async (id) => {
      let req = new SearchStringOrIntReq();
      req.SearchInt = id;
      let res = await AccountingApi.GetCurrentUserBalance(req);
      if (res.status == "Success") {
        setUserBalance(res.data);
      } else {
        Swal.fire({
          title: "Error",
          text: "Failed to get user balance: " + res.message,
          icon: "error",
          confirmButtonText: "Ok",
        });
      }
    };

    if (state?.pkg?.getPatient?.getUser?.id) {
      getUserBalance(state.pkg.getPatient.getUser.id);
    } else if (packageData?.getPatient?.getUser?.id) {
      getUserBalance(packageData.getPatient.getUser.id);
    }
  }, [packageData, state]);

  // Fetch receipts
  useEffect(() => {
    async function fetchReceipts() {
      try {
        const filters = {};
        const page = searchParams.get("page");
        if (page) filters.page = page;

        const response = await getTableRecordsForPackage(packageData.id, filters);
        setReceipts(response);
      } catch (error) {
        console.error("Error fetching receipts:", error);
      }
    }

    if (packageData && packageData.id) {
      fetchReceipts();
    }
  }, [searchParams, packageData]);

  // Fetch balance history - FIXED
  useEffect(() => {
    async function fetchBalanceHistory() {
      try {
        const filters = {};
        const page = searchParams.get("page");
        if (page) filters.page = page;

        console.log("Fetching balance history for package:", packageData.id);
        const response = await getBalanceHistoryForPackage(packageData.id, filters);

        if (response && response.status === "Success") {
          setBalanceHistory(response.data);
          console.log("Balance history fetched:", response.data);
        } else {
          console.error("Failed to fetch balance history:", response?.message);
        }
      } catch (error) {
        console.error("Error fetching balance history:", error);
      }
    }

    if (packageData && packageData.id) {
      fetchBalanceHistory();
    }
  }, [searchParams, packageData]);

  // Set loaded state
  useEffect(() => {
    if (Object.keys(packageData).length > 0) {
      setIsLoaded(true);
    }
  }, [packageData]);

  const sessionsTableRef = useRef(null);
  const addSessionsRef = useRef(null);
  useEffect(() => {
    if (showSessions && sessionsTableRef.current) {
      const y = sessionsTableRef.current.getBoundingClientRect().top + window.scrollY - 100; 

      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, [showSessions]);

  useEffect(() => {
    if (showAddSessions && addSessionsRef.current) {
      const y = addSessionsRef.current.getBoundingClientRect().top + window.scrollY - 100; 

      window.scrollTo({ top: y, behavior: "smooth" });
    }
  }, [showAddSessions]);

  if (!iIsLoaded && Object.keys(packageData).length === 0) {
    if (loadError) {
      return <div>Error loading contract data: {loadError}</div>;
    }
    return <div>Loading Contract Data...</div>;
  }

  return (
    <DashboardLayout>
      <DashboardNavbar />
      <PackageDetailsCard
        Package={packageData}
        setIsLoaded={setIsLoaded}
        setShowSessions={setShowSessions}
        ShowSessions={showSessions}
        setShowAddSessions={setShowAddSessions}
        ShowAddSessions={showAddSessions}
        userBalance={userBalance}
      />

      <NotesAndStartDate Package={packageData} setIsLoaded={setIsLoaded} />
      {packageData.postPaid && <PostPaid Package={packageData} setIsLoaded={setIsLoaded} />}
      <PackageStatistics Package={packageData} />

      {showSessions && (
        <div ref={sessionsTableRef}>
          <PackageSessionsTable
            packageData={packageData}
            setIsLoaded={setIsLoaded}
            setPackageData={setPackageData}
          />
        </div>
      )}

      {showAddSessions && (
        <div ref={addSessionsRef}>
          <AddSessionsToPackage Package={packageData} />
        </div>
      )}

      {balanceHistory && (
        <Card sx={{ height: "100%", boxShadow: !false && "none", marginBottom: "2em" }}>
          <BalanceHistoryTable
            balanceHistory={balanceHistory.list}
            totalCount={balanceHistory.totalItems}
            pageNumber={balanceHistory.pageNumber}
            pageSize={balanceHistory.pageSize}
          />
        </Card>
      )}

      {receipts && (
        <Card sx={{ height: "100%", boxShadow: !false && "none", marginBottom: "2em" }}>
          <ReceiptsTable
            receipts={receipts.items}
            totalCount={receipts.totalCount}
            pageNumber={receipts.pageNumber}
            pageSize={receipts.pageSize}
          />
        </Card>
      )}
    </DashboardLayout>
  );
};

export default Index;
